import type { CharacterSummary } from '@brimstone/shared';
import { Link } from 'react-router';
import { DataGrid, DataRow, EmptyState, PageLayout, Panel, Tag } from '../../components/ui';
import { formatAge } from '../../lib/time';
import { raceAndClass, useCharacters } from '../../lib/roster';

const COLUMNS = 'minmax(0, 1.4fr) minmax(0, 1.4fr) 48px 96px minmax(0, 1.4fr) minmax(0, 1fr)';

function Flags({ c }: { c: CharacterSummary }) {
  return (
    <span className="flex flex-wrap gap-1.5">
      {c.inPool && <Tag>POOL</Tag>}
      {c.duoOnly && (
        <span className="border border-line px-1.5 py-0.5 font-mono text-tag font-semibold text-muted">
          DUO ONLY
        </span>
      )}
      {!c.inRoster && (
        <span className="border border-line px-1.5 py-0.5 font-mono text-tag font-semibold text-muted">
          HIDDEN
        </span>
      )}
    </span>
  );
}

/** /book: the roster as a data grid (M4). */
export function BookIndexPage() {
  const roster = useCharacters();
  const characters = roster.data?.characters ?? [];
  return (
    <PageLayout
      main={
        <Panel title="Book" meta={roster.data ? `${characters.length} characters` : undefined}>
          {!roster.data ? (
            <EmptyState>{roster.isError ? 'Couldn’t load the roster.' : 'Loading…'}</EmptyState>
          ) : characters.length === 0 ? (
            <EmptyState>
              No characters yet: the roster plan is imported when the server starts.
            </EmptyState>
          ) : (
            <DataGrid
              label="Characters"
              columns={COLUMNS}
              header={['Character', 'Race and class', 'Lvl', 'Status', 'Flags', 'Last seen']}
            >
              {characters.map((c) => (
                <DataRow key={c.id} columns={COLUMNS}>
                  <span role="cell" className="min-w-0">
                    <Link
                      to={`/book/${c.id}`}
                      className="block min-h-11 content-center truncate text-name font-semibold text-text no-underline hover:text-accent"
                    >
                      {c.name}
                    </Link>
                  </span>
                  <span role="cell" className="truncate text-secondary text-muted">
                    {raceAndClass(c)}
                  </span>
                  <span role="cell" className="font-mono">
                    {c.level ?? '—'}
                  </span>
                  <span role="cell" className="font-mono text-label uppercase text-muted">
                    {c.status}
                  </span>
                  <span role="cell">
                    <Flags c={c} />
                  </span>
                  <span role="cell" className="font-mono text-secondary text-muted">
                    {c.lastSeenAt ? formatAge(c.lastSeenAt) : 'planned'}
                  </span>
                </DataRow>
              ))}
            </DataGrid>
          )}
        </Panel>
      }
    />
  );
}
